// src/utils/color.ts

/**
 * Robust color validation using CSS.supports when available,
 * with regex fallbacks for older engines.
 */
export function isColor(value: string): boolean {
  if (!value) return false;
  const v = value.trim();
  if (!v) return false;

  // Best, future-proof path:
  try {
    if (typeof CSS !== "undefined" && CSS.supports && CSS.supports("color", v)) {
      return true;
    }
  } catch {
    // ignore
  }

  const lower = v.toLowerCase();

  // Hex: #rgb, #rgba, #rrggbb, #rrggbbaa
  if (/^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(lower)) return true;

  // rgb()/rgba() — allow commas or space+slash alpha
  // Examples: rgb(255,0,0) rgba(255,0,0,0.5) rgb(255 0 0 / 50%)
  if (/^rgba?\(\s*[^)]+\)$/.test(lower)) return true;
  if (/^rgb\(\s*[^)]+\/\s*[^)]+\)$/.test(lower)) return true;

  // hsl()/hsla() — commas or space+slash alpha
  if (/^hsla?\(\s*[^)]+\)$/.test(lower)) return true;
  if (/^hsl\(\s*[^)]+\/\s*[^)]+\)$/.test(lower)) return true;

  // CSS variable
  if (/^var\(--[\w-]+\)$/.test(lower)) return true;

  // Common named colors (kept minimal; CSS.supports covers full list when available)
  const named = new Set([
    "black","white","red","blue","gray","grey","green","yellow",
    "orange","purple","pink","brown","cyan","magenta","lime",
    "silver","maroon","navy","teal","olive","transparent","inherit","currentcolor"
  ]);
  return named.has(lower);
}

/**
 * Normalize a color to something safe for CSS.
 * Falls back if invalid.
 */
export function normalizeColor(value: string, fallback = "inherit"): string {
  return isColor(value) ? value : fallback;
}

/**
 * Detect if the given color is effectively transparent (alpha == 0).
 * Handles:
 *  - 'transparent'
 *  - #rgba / #rrggbbaa where alpha nibble/byte is 0
 *  - rgba()/hsla() with last alpha = 0 (comma syntax)
 *  - rgb()/hsl() with slash alpha `/ 0` (space+slash syntax)
 */
export function isTransparent(value: string): boolean {
  if (!value) return false;
  const v = value.trim();
  if (!v) return false;

  const lower = v.toLowerCase();

  if (lower === "transparent") return true;

  // Hex alpha = 0
  // #RGBA -> alpha is 4th hex (1 nibble); #RRGGBBAA -> last 2 hex
  const hex = lower.match(/^#([0-9a-f]{4}|[0-9a-f]{8})$/i);
  if (hex) {
    const h = hex[1];
    // 4-digit: rgba => A is h[3]; 8-digit: rrggbbaa => AA is last two
    const alphaNibble = h.length === 4 ? h[3] : h.slice(6, 8);
    if ((h.length === 4 && alphaNibble === "0") || (h.length === 8 && alphaNibble === "00")) {
      return true;
    }
    return false;
  }

  // rgba()/hsla() comma syntax: ensure final component (alpha) is zero
  // e.g., rgba(10,20,30, 0)   hsla(120,50%,50%, 0.0)
  if (/(?:rgba|hsla)\(\s*[^)]*,\s*0(?:\.0+)?\s*\)$/i.test(lower)) return true;

  // rgb()/hsl() space syntax with slash alpha: e.g., rgb(10 20 30 / 0)
  if (/(?:rgb|hsl)\(\s*[^)]*\/\s*0(?:\.0+)?\s*\)$/i.test(lower)) return true;

  // We do NOT treat plain rgb()/hsl() without alpha as transparent
  // nor color() / lab() / lch() here — those can be handled via CSS.supports if needed.

  return false;
}